import { debugLog } from '../utils/debugLog';
import { createClient } from '@supabase/supabase-js';

// In production: window.__ENV__ is injected by server.js at runtime so vars
// work even though Railway doesn't pass service variables to the build step.
// In development: process.env.REACT_APP_* are baked in by CRA from .env.
const _w = typeof window !== 'undefined' ? window : {};
const supabaseUrl  = _w.__ENV__?.REACT_APP_SUPABASE_URL  || process.env.REACT_APP_SUPABASE_URL  || '';
const supabaseAnon = _w.__ENV__?.REACT_APP_SUPABASE_ANON_KEY || process.env.REACT_APP_SUPABASE_ANON_KEY || '';

if (!supabaseUrl || !supabaseAnon) {
  console.warn('[Supabase] vars missing — cloud features disabled (check Railway Variables tab)');
}

debugLog('[Supabase] URL:', supabaseUrl ? supabaseUrl.slice(0, 40) : 'NOT SET');
debugLog('[Supabase] Key starts with:', supabaseAnon ? supabaseAnon.slice(0, 10) : 'NOT SET');

// Network requests fail fast instead of hanging forever. A paused Supabase
// free-tier project (or a blocked network) makes fetch() hang indefinitely —
// the browser has no built-in timeout — which used to freeze getSession().
const FETCH_TIMEOUT_MS = 30000;
const fetchWithTimeout = (url, options = {}) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  // Respect a caller-provided signal in addition to our timeout.
  if (options.signal) {
    if (options.signal.aborted) controller.abort();
    else options.signal.addEventListener('abort', () => controller.abort(), { once: true });
  }
  return fetch(url, { ...options, signal: controller.signal })
    .finally(() => clearTimeout(timer));
};

// Cross-tab auth lock, bounded so it can never deadlock. The default Supabase
// navigatorLock can hang a tab forever if another tab holds the lock during a
// stuck refresh; a plain no-op lock removes that hang but then lets two tabs
// refresh the (single-use, rotating) refresh token at once, invalidating each
// other. This bounded lock serializes refreshes across tabs in the normal case
// but gives up after a timeout and proceeds rather than hanging.
// Long enough for a slow-network token refresh to complete in tab A before
// tab B gives up waiting and tries to refresh independently (which would
// invalidate tab A's single-use refresh token and force a re-login).
const LOCK_ACQUIRE_TIMEOUT_MS = 10000;
const boundedLock = async (name, acquireTimeout, fn) => {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : null;
  if (!locks?.request) return fn(); // Web Locks unsupported — run directly

  const controller = new AbortController();
  const timeoutMs = acquireTimeout > 0 ? acquireTimeout : LOCK_ACQUIRE_TIMEOUT_MS;
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await locks.request(`sb-auth:${name}`, { signal: controller.signal }, () => fn());
  } catch (err) {
    if (err?.name === 'AbortError') {
      console.warn('[Supabase] auth lock acquire timed out — proceeding without lock');
      return fn();
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
};

// The advisor app and the client portal run as the same SPA on the same origin
// (the portal is just the /portal route), so they share localStorage. With a
// single client + default storageKey, logging into the portal as a client
// OVERWRITES the advisor's session — which made AuthContext fire
// "user changed → clear local data" and wipe the advisor's IndexedDB.
//
// Fix: give the portal its own storageKey, but ROUTE-GATE the two clients so
// only ONE GoTrueClient is ever instantiated per page:
//   - advisor / plan routes → advisor client only (default storageKey)
//   - /portal route         → portal client only (own storageKey)
// This keeps the advisor app's auth behavior byte-for-byte identical to before
// (no second client on the page) while a client login can never touch the
// advisor session.
const hasEnv = !!(supabaseUrl && supabaseAnon);
const isPortalRoute = typeof window !== 'undefined'
  && window.location.pathname.startsWith('/portal');

function createSupabaseClient({ storageKey } = {}) {
  return createClient(supabaseUrl, supabaseAnon, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      ...(storageKey ? { storageKey } : {}),
      lock: boundedLock,
    },
    global: { fetch: fetchWithTimeout },
  });
}

// Advisor app client — default storageKey (preserves existing advisor sessions).
// null on the portal route, where AuthContext deliberately no-ops.
export const supabase = (hasEnv && !isPortalRoute)
  ? createSupabaseClient()
  : null;

// Client-portal client — isolated under its own storageKey. Only instantiated
// on the /portal route.
export const supabasePortal = (hasEnv && isPortalRoute)
  ? createSupabaseClient({ storageKey: 'sb-portal-auth-token' })
  : null;
